import { useState } from "react";
import { toast } from "sonner";
import { Handshake } from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";

const CATEGORIES = [
  "Academic Institutions", "Schools", "Music Academies", "Educators",
  "Technology Companies", "Corporate Organisations", "Content Partners",
  "Event Partners", "Strategic Investors",
];

const inputCls =
  "w-full rounded-lg border border-white/15 bg-navy-900/60 px-4 py-2.5 text-sm text-ivory placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30";

export default function Partnerships() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", category: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partnerships", form);
      setSubmitted(true);
      toast.success("Partnership enquiry submitted.");
    } catch {
      toast.error("Could not submit your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partnerships-page">
      <PageHero
        eyebrow="Partner With RCS"
        title="Let's build the future of education together."
        lede="We welcome conversations with institutions, educators, technology companies and strategic partners who share our vision for better learning."
      />

      <section className="py-24 lg:py-32" data-testid="partnership-categories">
        <div className="container-rcs">
          <Section eyebrow="Partnership Opportunities" title="Ways to collaborate with RCS." />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <div className="flex items-center gap-4 rounded-2xl border border-navy-900/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50" data-testid={`partner-cat-${i}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy-950 text-gold">
                    <Handshake className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="font-serif text-lg text-navy-900">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy-950 py-24 lg:py-32" data-testid="partnership-form-section">
        <div className="container-rcs grid gap-16 lg:grid-cols-2">
          <Section
            dark
            eyebrow="Explore Partnership Opportunities"
            title="Start the conversation."
            lede="Tell us about your organisation and the collaboration you have in mind. Our team reviews every partnership enquiry."
          />
          <Reveal delay={0.15}>
            {submitted ? (
              <div className="rounded-2xl border border-gold/30 bg-navy-900/60 p-10 text-center" data-testid="partnership-success">
                <h3 className="font-serif text-2xl text-ivory">Thank you.</h3>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                  Your partnership enquiry has been received. We will be in touch shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/10 bg-navy-900/40 p-8" data-testid="partnership-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input required aria-label="Name" placeholder="Full Name *" className={inputCls} value={form.name} onChange={set("name")} data-testid="partner-name" />
                  <input required type="email" aria-label="Email" placeholder="Email *" className={inputCls} value={form.email} onChange={set("email")} data-testid="partner-email" />
                </div>
                <input aria-label="Organisation" placeholder="Organisation" className={inputCls} value={form.organisation} onChange={set("organisation")} data-testid="partner-organisation" />
                <select aria-label="Partnership category" className={inputCls} value={form.category} onChange={set("category")} data-testid="partner-category">
                  <option value="">Select partnership category</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <textarea required aria-label="Message" placeholder="Tell us about the opportunity *" rows={5} className={inputCls} value={form.message} onChange={set("message")} data-testid="partner-message" />
                <button type="submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="partner-submit">
                  {submitting ? "Submitting…" : "Submit Enquiry"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
